import React from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Bar,
  BarChart,
  ReferenceLine,
  Cell,
  ReferenceArea,
  Text,
} from 'recharts';
import AxisY from '../AxisY';
import LegendContent from '../LegendContent';
import TooltipContent from '../TooltipContent';
import ReferenceLineLabel from '../ReferenceLineLabel';
import AnnotationLineIcon from '../AnnotationLineIcon';
import formatData from '../../methods/formatData';
import generateComparisionColors from '../../methods/generateComparisionColors';

/* eslint-disable react/no-array-index-key */
const BarChartContent = ({
  framed,
  data,
  seriesConfig,
  dateOnXAxis,
  isStacked,
  isVertical,
  labelX,
  labelSeries,
  syncId,
  compareMode,
  lineColors,
  t,
  disableAnimations,
  referenceLine,
  showWhiteSpace,
  selectedAnnotationName,
  onClick,
  withAnnotations,
  smallerTooltip,
  hideTooltip,
  withNowElement,
  chartId,
  hiddenData,
}) => {
  const compareColors = compareMode.length
    ? compareMode.map((mode, index) => (
      generateComparisionColors(
        {
          number: seriesConfig.length,
          index,
        },
      )
    )) : [];

  const renderHiddenTooltipText = (hidden, p) => (hidden && hidden.fromName
    && (data.findIndex(el => el.name === p.label) >= data.findIndex(el => el.name === hidden.fromName) && data.findIndex(el => el.name === p.label) <= data.findIndex(el => el.name === hidden.toName))
    ? hidden.tooltipText : null);

  const changeHiddenBarsColor = (cellIndex) => {
    if (hiddenData && (hiddenData.findIndex(item => (data.findIndex(el => el.name === item.fromName) <= cellIndex)
      && (data.findIndex(el => el.name === item.toName) >= cellIndex)) > -1)) {
      return {
        fill: '#E6E6E6',
        filter: 'url(#blur)',
      };
    }

    return {};
  };

  return (
    <BarChart
      data={data}
      margin={{
        top: framed ? 26 : 0,
        right: seriesConfig.find(serie => (serie.axisY === 'right')) ? 0 : 24,
        left: 0,
        bottom: labelX && !isVertical ? 26 : 8,
      }}
      syncId={syncId ? `${syncId}_${data.length}` : undefined}
      onClick={onClick}
      layout={isVertical ? 'vertical' : 'horizontal'}
    >
      <defs>
        <filter id="blur">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>
      {!hideTooltip && (
        <Tooltip
          content={p => TooltipContent({
            ...p,
            dateOnXAxis,
            chartType: 'bar',
            compareMode,
            compareDivider: seriesConfig.length,
            t,
            showWhiteSpace,
            smallerTooltip,
            hiddenContent: hiddenData && hiddenData.map(item => renderHiddenTooltipText(item, p)).join(''),
          })}
          cursor={{ fill: '#EEEEEE' }}
        />
      )}
      {
        hiddenData
          ? hiddenData.map(item => (
            <ReferenceArea x1={item.fromName} x2={item.toName} yAxisId="left" stroke="#F7F7F7" fill="#F7F7F7" fillOpacity={1} />
          ))
          : null
      }
      {isVertical ? (
        <YAxis
          dataKey="name"
          fontSize="10px"
          stroke="#131313"
          tickLine={false}
          width={100}
          tickFormatter={tick => formatData(tick, dateOnXAxis, false, false, t)}
          label={{
            value: labelX,
            position: 'insideLeft',
            fontSize: '10px',
            angle: -90,
            offset: 10,
            style: { textAnchor: 'middle' },
          }}
          type="category"
          tick={props => <Text {...props} breakAll maxLines={2} width={80}>{props.tickFormatter(props.payload.value)}</Text>}
        />
      ) : (
        <XAxis
          dataKey="name"
          fontSize="10px"
          stroke="#131313"
          tickLine={false}
          tickMargin={7}
          tickFormatter={tick => formatData(tick, dateOnXAxis, false, false, t)}
          label={{
            value: labelX,
            position: 'insideBottom',
            fontSize: '10px',
            offset: -9,
          }}
        />
      )}
      {AxisY({ seriesConfig, isOnRight: false, t, isVertical })}
      {AxisY({ seriesConfig, isOnRight: true, t, isVertical })}
      <CartesianGrid stroke="#EEEEEE" strokeWidth={1} vertical={isVertical} horizontal={!isVertical} />
      <Legend
        content={p => LegendContent({
          ...p,
          framed,
          chartType: 'bar',
          labelSeries,
          compareMode,
          compareDivider: seriesConfig.length,
        })}
        verticalAlign="top"
        wrapperStyle={{ width: '100%', top: '0' }}
      />
      <mask id={`Mask-${chartId}`}>
        <linearGradient id={`maskGradient-${chartId}`} x1="0" y1="0" x2="0.05" y2="0" gradientTransform="rotate(45)" gradientUnits="userSpaceOnUse" spreadMethod="repeat">
          <stop offset="0%" stopColor="#808080" />
          <stop offset="25%" stopColor="#808080" />
          <stop offset="35%" stopColor="white" />
          <stop offset="100%" stopColor="white" />
        </linearGradient>
        <rect x="0" y="0" width="100%" height="100%" fill={`url(#maskGradient-${chartId})`} />
      </mask>
      {compareMode.length
        ? compareMode.map((mode, indexMode) => (
          seriesConfig.map((serie, index) => (
            <Bar
              yAxisId={isVertical ? undefined : serie.axisY}
              xAxisId={isVertical ? serie.axisY : undefined}
              stackId={isStacked ? `${serie.axisY}${indexMode}` : null}
              dataKey={indexMode ? serie[`id${indexMode}`] : serie.id}
              name={serie.name}
              unit={serie.unit}
              fill={compareColors[indexMode][index % lineColors.length]}
              key={indexMode ? `${serie[`id${indexMode}`]}_${index}` : `${serie.id}_${index}`}
              barSize={120}
              isAnimationActive={!disableAnimations}
            >
              {
                data.map((entry, cellIndex) => (
                  <Cell
                    key={indexMode ? `${serie[`id${indexMode}`]}_${index}_${cellIndex}` : `${serie.id}_${index}_${cellIndex}`}
                    mask={withNowElement && data.length - 1 === cellIndex ? `url(#Mask-${chartId})` : ''}
                    {...changeHiddenBarsColor(cellIndex)}
                  />
                ))
              }
            </Bar>
          ))
        ))
        : seriesConfig.map((serie, index) => (
          <Bar
            yAxisId={isVertical ? undefined : serie.axisY}
            xAxisId={isVertical ? serie.axisY : undefined}
            stackId={isStacked ? serie.axisY : null}
            dataKey={serie.id}
            name={serie.name}
            unit={serie.unit}
            fill={
              serie.color
                ? lineColors.filter(item => item.name === serie.color)[0].value
                : lineColors[index % lineColors.length].value
            }
            key={`${serie.id}_${index}`}
            barSize={120}
            isAnimationActive={!disableAnimations}
          >
            {
              data.map((entry, cellIndex) => (
                <Cell
                  key={`${serie.id}_${index}_${cellIndex}`}
                  mask={withNowElement && data.length - 1 === cellIndex ? `url(#Mask-${chartId})` : ''}
                  {...changeHiddenBarsColor(cellIndex)}
                />
              ))
            }
          </Bar>
        ))
      }
      {
      referenceLine && (Array.isArray(referenceLine) ? referenceLine : [referenceLine]).map(el => (
        <ReferenceLine
          key={el.value}
          y={isVertical ? undefined : el.value}
          x={isVertical ? el.value : undefined}
          label={<ReferenceLineLabel label={el.label} />}
          stroke="#131313"
          strokeDasharray="3 1"
          yAxisId={isVertical ? undefined : 'left'}
          xAxisId={isVertical ? 'left' : undefined}
        />
      ))
      }
      {
        [
          ...data.filter(el => el.numberOfAnnotations > 0 && selectedAnnotationName !== el.name),
          ...data.filter(el => el.numberOfAnnotations > 0 && selectedAnnotationName === el.name), // Selected on top
        ]
          .map(el => (
            withAnnotations ? (
              <ReferenceLine
                key={el.name}
                x={isVertical ? undefined : el.name}
                y={isVertical ? el.name : undefined}
                label={<AnnotationLineIcon selected={selectedAnnotationName === el.name} />}
                stroke="#CCCCCC"
                opacity={Number(selectedAnnotationName === el.name)}
                yAxisId={isVertical ? undefined : 'left'}
                xAxisId={isVertical ? 'left' : undefined}
              />
            ) : null
          ))
      }
    </BarChart>
  );
};

export default BarChartContent;
